import { Button } from '@avanta/shared/components/ui/Button';
import { Card } from '@avanta/shared/components/ui/Card';
import { Input } from '@avanta/shared/components/ui/Input';
import { LOCIZE_NS, Languages } from '@avanta/shared/constants/common';
import { FIELD_VARIANT } from '@avanta/shared/constants/forms';
import { useFetchPatient } from '@avanta/shared/hooks/useFetchPatient';
import { type Patient } from '@avanta/shared/types/patients';
import { type User } from '@avanta/shared/types/users';
import { cn } from '@avanta/shared/utils/cn';
import { dateFromBirthParts, getBirthDateParts } from '@avanta/shared/utils/date';
import { Building, Pencil, Save, UserCog, X } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { EMPTY_VALUE } from 'constants/common';
import { InsuredPersonProfileFormSkeleton } from './InsuredPersonProfileFormSkeleton';
import { useFetchPatientHealthCenter } from './hooks/useFetchPatientHealthCenter';
import { useUpdateInsuredPerson } from './hooks/useUpdateInsuredPerson';
import {
  insuredPersonProfileForm,
  type InsuredPersonProfileForm as InsuredPersonProfileFormType,
} from './model';

interface InsuredPersonProfileFormContentProps {
  healthCenterName?: string | null;
  insuredPerson: User;
  patientId: string;
}

const InsuredPersonProfileFormContent = ({
  healthCenterName,
  insuredPerson,
  patientId,
}: InsuredPersonProfileFormContentProps): React.ReactElement => {
  const { mutate: updateInsuredPerson, isPending } = useUpdateInsuredPerson(patientId);
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const [isEditing, setIsEditing] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InsuredPersonProfileFormType>({
    mode: 'onChange',
    defaultValues: {
      firstName: insuredPerson.firstName ?? '',
      surname: insuredPerson.surname ?? '',
      email: insuredPerson.email ?? '',
      nif: insuredPerson.nif ?? '',
      contactPhone: insuredPerson.contactPhone ?? '',
      gender: insuredPerson.gender?.toString() ?? '',
      dateOfBirth: dateFromBirthParts(
        insuredPerson.monthOfBirth,
        insuredPerson.yearOfBirth,
        insuredPerson.dayOfBirth
      ),
    },
  });

  const handleOnCancel = (): void => {
    reset();
    setIsEditing(false);
  };

  const handleOnSubmit = async (data: InsuredPersonProfileFormType): Promise<void> => {
    const { dateOfBirth, ...formValues } = data;
    const patient = {
      ...insuredPerson,
      ...formValues,
      gender: Number(data.gender),
      spokenLanguages: [Languages.Spanish],
      ...(dateOfBirth && getBirthDateParts(dateOfBirth)),
    } as Patient;
    updateInsuredPerson(patient, {
      onSuccess: () => {
        reset(data);
        setIsEditing(false);
      },
    });
  };

  const healthCenterDisplay = healthCenterName ?? EMPTY_VALUE;

  return (
    <Card
      variant="primary"
      title={t('InsuredPersonsProfile.info.heading')}
      icon={UserCog}
      className="am-md:p-0"
    >
      <form className="flex flex-col" onSubmit={handleSubmit(handleOnSubmit)}>
        <div className="grid grid-cols-3 gap-6 px-8 pt-8 pb-6">
          {Object.entries(insuredPersonProfileForm).map(([id, input]) => {
            const { component: Component } = input;
            const formId = id as keyof typeof insuredPersonProfileForm;
            const formErrors = errors[formId];
            const isControllerComponent = Component !== Input;
            const rules = {
              required: input.required,
              ...(input.pattern && { pattern: input.pattern }),
              ...(input.validate && { validate: input.validate }),
              ...(input.minLength && { minLength: input.minLength }),
              ...(input.maxLength && { maxLength: input.maxLength }),
            };
            const helperText = formErrors?.message ? t(formErrors.message) : undefined;
            const fieldVariant = formErrors
              ? FIELD_VARIANT.INVALID
              : isEditing
                ? FIELD_VARIANT.EDIT
                : FIELD_VARIANT.FILLED;

            return (
              <div key={id}>
                {isControllerComponent ? (
                  <Component
                    control={control}
                    name={formId}
                    className="w-full"
                    label={t(input.label)}
                    placeholder={t(input.placeholder ?? '')}
                    options={input.options ?? []}
                    rules={rules}
                    variant={fieldVariant}
                    helperText={helperText}
                    leftIcon={input.leftIcon}
                    defaultCountry="ES"
                    readOnly={!isEditing}
                  />
                ) : (
                  <Input
                    type={input.type}
                    className="w-full"
                    label={t(input.label)}
                    placeholder={t(input.placeholder ?? '')}
                    readOnly={!isEditing}
                    variant={fieldVariant}
                    helperText={helperText}
                    {...register(formId, rules)}
                  />
                )}
              </div>
            );
          })}
          <Input
            type="text"
            className="w-full"
            label={t('InsuredPersonsProfile.healthCenter.label')}
            value={healthCenterDisplay}
            leftIcon={Building}
            readOnly
            variant={isEditing ? FIELD_VARIANT.EDIT : FIELD_VARIANT.FILLED}
          />
        </div>
        <div className={cn('flex justify-end px-4 pb-4', isEditing && 'pb-8 pr-8 pt-2')}>
          {isEditing ? (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="destructive_terciary"
                size="lg"
                icon="leading"
                onClick={handleOnCancel}
              >
                <X />
                {t('InsuredPersonsProfile.cancel.cta')}
              </Button>
              <Button
                type="submit"
                variant="secondary"
                size="lg"
                icon="leading"
                isLoading={isPending}
              >
                <Save />
                {t('InsuredPersonsProfile.save.cta')}
              </Button>
            </div>
          ) : (
            <Button type="button" variant="link" icon="leading" onClick={() => setIsEditing(true)}>
              <Pencil className="size-4" />
              {t('InsuredPersonsProfile.edit.cta')}
            </Button>
          )}
        </div>
      </form>
    </Card>
  );
};

export const InsuredPersonProfileForm = (): React.ReactElement => {
  const params = useParams();
  const patientId = params?.id ?? '';
  const { data: insuredPerson, isLoading: isLoadingInsuredPerson } = useFetchPatient(patientId);
  const { data: healthCenterName, isLoading: isLoadingHealthCenter } = useFetchPatientHealthCenter(
    insuredPerson?.departmentId ?? ''
  );

  const isLoading = isLoadingInsuredPerson || isLoadingHealthCenter;

  if (isLoading || !insuredPerson) {
    return <InsuredPersonProfileFormSkeleton />;
  }

  return (
    <InsuredPersonProfileFormContent
      healthCenterName={healthCenterName}
      insuredPerson={insuredPerson}
      patientId={patientId}
    />
  );
};
